import {react, useState, useEffect} from 'react';
import AdminSidebar from '../components/admin/AdminSidebar.js';
import getAPI from '../functions/API/getAPI.js'
import postAPI from '../functions/API/postAPI.js'
import { toast } from '../lib/toast.js';
import Loader from '../components/loader.js';
import { CheckCircleIcon, XCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

export default function SsoSettings() {
    const [config, setConfig] = useState({
        enabled: false,
        tenant_id: '',
        client_id: '',
        client_secret: '',
        allowed_domains: ''
    });
    const [loading, setLoading] = useState(true);
    const [isLoaded, setIsLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    // Get the redirect URI for display
    const redirectUri = `${window.location.origin}/sso/callback`;

    async function getSsoConfig() {
        try {
            const response = await getAPI('admin/sso/config');
            if (response.response.ok && response.data) {
                setConfig({
                    enabled: response.data.enabled || false,
                    tenant_id: response.data.tenant_id || '',
                    client_id: response.data.client_id || '',
                    client_secret: '', // Never returned from API for security
                    allowed_domains: response.data.allowed_domains ? response.data.allowed_domains.join(', ') : ''
                });
            }
        } catch (error) {
            console.error('Failed to load SSO config:', error);
        }
        setIsLoaded(true);
    }

    function handleChange(event) {
        const { name, value, type, checked } = event.target;
        setConfig(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setHasChanges(true);
    }

    async function saveConfig() {
        setSaving(true);
        try {
            // Parse allowed_domains from comma-separated string to array
            const allowedDomainsArray = config.allowed_domains
                ? config.allowed_domains.split(',').map(d => d.trim()).filter(d => d)
                : null;

            const body = {
                enabled: config.enabled,
                tenant_id: config.tenant_id,
                client_id: config.client_id,
                allowed_domains: allowedDomainsArray
            };

            // Only include client_secret if it was changed (not empty)
            if (config.client_secret) {
                body.client_secret = config.client_secret;
            }

            const response = await postAPI('admin/sso/config', body);
            if (response.response.ok) {
                toast.success('SSO configuration saved successfully.');
                setHasChanges(false);
                // Clear the client secret field after successful save
                setConfig(prev => ({ ...prev, client_secret: '' }));
            } else {
                toast.error(response.data?.message || 'Failed to save SSO configuration.');
            }
        } catch (error) {
            toast.error('An error occurred while saving SSO configuration.');
        }
        setSaving(false);
    }

    function copyToClipboard(text) {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard!');
    }

    useEffect(() => {
        getSsoConfig();
    }, []);

    return (
        <AdminSidebar>
            {loading ? (
                <Loader style='form' setLoadingState={setLoading} isLoaded={isLoaded}/>
            ) : (
                <>
                    <div className="sm:flex-auto mb-8 border-b border-gray-900/10 pb-8">
                        <h1 className="text-2xl text-left leading-6 text-gray-900 col-span-3">SSO Settings</h1>
                        <p className="mt-2 text-sm text-gray-700">
                            Configure Microsoft Entra ID Single Sign-On for admin users
                        </p>

                        <div className="mt-4 rounded-md bg-gray-50 p-4">
                            <h3 className="text-sm font-medium text-gray-800">About Entra ID SSO</h3>
                            <p className="mt-2 text-sm text-gray-600">
                                Single Sign-On allows administrators to sign in using their Microsoft work account, leveraging your organisation's existing identity management and multi-factor authentication policies.
                            </p>
                            <p className="mt-2 text-sm text-gray-600">
                                This integration uses industry-standard OAuth 2.0 with PKCE (Proof Key for Code Exchange), providing protection against authorization code interception and other common attack vectors. All tokens are cryptographically validated against Microsoft's public keys before granting access.
                            </p>
                        </div>
                    </div>

                    <form className="dark:bg-white h-full">
                        <div className="space-y-6 mb-10">
                            {/* Enable/Disable SSO */}
                            <div className="border-b border-gray-900/10 pb-8">
                                <h2 className="text-base font-semibold leading-7 text-gray-900">SSO Status</h2>
                                <p className="mt-1 text-sm leading-6 text-gray-600">
                                    Enable or disable Microsoft Entra ID SSO for admin users
                                </p>

                                <div className="mt-6">
                                    <div className="relative flex gap-x-3">
                                        <div className="flex h-6 items-center">
                                            <input
                                                id="enabled"
                                                name="enabled"
                                                type="checkbox"
                                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                                                onChange={handleChange}
                                                checked={config.enabled}
                                            />
                                        </div>
                                        <div className="text-sm leading-6">
                                            <label htmlFor="enabled" className="font-medium text-gray-900">
                                                Enable SSO
                                            </label>
                                            <p className="text-gray-500">
                                                When enabled, admins marked as "SSO Only" will sign in with Microsoft
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Azure App Registration Info */}
                            <div className="border-b border-gray-900/10 pb-8">
                                <h2 className="text-base font-semibold leading-7 text-gray-900">Azure App Registration</h2>
                                <p className="mt-1 text-sm leading-6 text-gray-600">
                                    Enter the credentials from your Microsoft Entra ID app registration
                                </p>

                                <div className="mt-6 rounded-md bg-blue-50 p-4">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <InformationCircleIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
                                        </div>
                                        <div className="ml-3">
                                            <h3 className="text-sm font-medium text-blue-800">Redirect URI</h3>
                                            <div className="mt-2 text-sm text-blue-700">
                                                <p>Add this redirect URI to your Azure app registration:</p>
                                                <div className="mt-1 flex items-center gap-2">
                                                    <code className="bg-blue-100 px-2 py-1 rounded text-xs">{redirectUri}</code>
                                                    <button
                                                        type="button"
                                                        onClick={() => copyToClipboard(redirectUri)}
                                                        className="text-blue-600 hover:text-blue-800 text-xs underline"
                                                    >
                                                        Copy
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                                    <div className="sm:col-span-4">
                                        <label htmlFor="tenant_id" className="block text-sm font-medium leading-6 text-gray-900">
                                            Tenant ID (Directory ID)
                                        </label>
                                        <div className="mt-2">
                                            <input
                                                type="text"
                                                id="tenant_id"
                                                name="tenant_id"
                                                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                                value={config.tenant_id}
                                                onChange={handleChange}
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                                            />
                                        </div>
                                    </div>

                                    <div className="sm:col-span-4">
                                        <label htmlFor="client_id" className="block text-sm font-medium leading-6 text-gray-900">
                                            Client ID (Application ID)
                                        </label>
                                        <div className="mt-2">
                                            <input
                                                type="text"
                                                id="client_id"
                                                name="client_id"
                                                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                                value={config.client_id}
                                                onChange={handleChange}
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                                            />
                                        </div>
                                    </div>

                                    <div className="sm:col-span-4">
                                        <label htmlFor="client_secret" className="block text-sm font-medium leading-6 text-gray-900">
                                            Client Secret
                                        </label>
                                        <div className="mt-2">
                                            <input
                                                type="password"
                                                id="client_secret"
                                                name="client_secret"
                                                placeholder={config.tenant_id ? '******** (leave blank to keep existing)' : 'Enter client secret'}
                                                value={config.client_secret}
                                                onChange={handleChange}
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                                            />
                                        </div>
                                        <p className="mt-1 text-xs text-gray-500">
                                            The client secret is encrypted and never displayed after saving
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Domain Restriction */}
                            <div className="border-b border-gray-900/10 pb-8">
                                <h2 className="text-base font-semibold leading-7 text-gray-900">Domain Restriction (Optional)</h2>
                                <p className="mt-1 text-sm leading-6 text-gray-600">
                                    Restrict SSO login to specific email domains
                                </p>

                                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                                    <div className="sm:col-span-4">
                                        <label htmlFor="allowed_domains" className="block text-sm font-medium leading-6 text-gray-900">
                                            Allowed Domains
                                        </label>
                                        <div className="mt-2">
                                            <input
                                                type="text"
                                                id="allowed_domains"
                                                name="allowed_domains"
                                                placeholder="example.com, company.org"
                                                value={config.allowed_domains}
                                                onChange={handleChange}
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                                            />
                                        </div>
                                        <p className="mt-1 text-xs text-gray-500">
                                            Comma-separated list of domains. Leave blank to allow all domains.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Setup Instructions */}
                            <div className="pb-8">
                                <h2 className="text-base font-semibold leading-7 text-gray-900">Setup Instructions</h2>
                                <div className="mt-4 prose prose-sm text-gray-600">
                                    <ol className="list-decimal list-inside space-y-2">
                                        <li>Go to Azure Portal &rarr; Microsoft Entra ID &rarr; App registrations</li>
                                        <li>Create a new registration or select an existing app</li>
                                        <li>Copy the Application (client) ID and Directory (tenant) ID</li>
                                        <li>Under Certificates & secrets, create a new client secret</li>
                                        <li>Under Authentication, add the redirect URI shown above (Web platform)</li>
                                        <li>Ensure ID tokens are enabled under Authentication</li>
                                        <li>Under API permissions, add: openid, profile, email (delegated)</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </form>

                    {hasChanges && (
                        <div className="fixed bottom-5 right-5">
                            <button
                                onClick={saveConfig}
                                disabled={saving}
                                className="mb-4 rounded-md bg-blue-700 px-3 py-2 font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700 disabled:opacity-50"
                            >
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    )}

                </>
            )}
        </AdminSidebar>
    );
}
